import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Row,
  Col,
  Form,
  Button,
  Table,
  Badge,
  Modal,
  ProgressBar,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PrivateLayout from '../../layouts/PrivateLayout';
import Panel from '../../common/Panel';
import Pagination from '../../common/Pagination';
import {
  useFetchFailedVaCalls,
  applyFailedQueueRating,
  retryFailedQueueClaim,
  retryAllFailedQueueClaims,
  useFetchJobStatus,
} from '../../../services/vaCallAttempts';
import { COLUMNS } from '../../../components/dashboard/admin/SalesDashboard';
import { useUser } from '../../../contexts/UserContext';

const ownerRoleLabel = (role) => {
  if (role === 'case_manager') return 'Case Manager';
  if (role === 'onboarding_specialist') return 'Onboarding Specialist';
  return 'Onboarding Admin';
};

const getJobProgressPercent = (jobStatus) => {
  if (!jobStatus?.totalItems) return 0;
  return Math.round((Number(jobStatus.processedItems || 0) / Number(jobStatus.totalItems || 1)) * 100);
};

const RATING_OPTIONS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

const ClaimRetryButton = ({ claimId, disabled = false }) => {
  const queryClient = useQueryClient();
  const [jobId, setJobId] = useState(null);
  const terminalHandledRef = useRef('');
  const { data: jobStatus } = useFetchJobStatus(jobId);

  useEffect(() => {
    if (!jobId || !jobStatus) return;
    if (jobStatus.jobStatus !== 'completed' && jobStatus.jobStatus !== 'failed') return;

    const terminalKey = `${jobId}:${jobStatus.jobStatus}:${jobStatus.completedAt || ''}`;
    if (terminalHandledRef.current === terminalKey) return;
    terminalHandledRef.current = terminalKey;

    if (jobStatus.jobStatus === 'completed') {
      const result = jobStatus.result || {};
      if (result.removedFromQueue) {
        toast.success('Retry succeeded. Claim should leave the failed queue with reconciliation resolved.');
      } else if (result.state === 'skipped') {
        toast.info(result.message || 'Retry skipped.');
      } else if (result.callResult) {
        toast.info(`Retry completed with ${result.callResult}. Claim remains in the failed queue.`);
      } else {
        toast.info(result.message || 'Retry completed.');
      }
    } else {
      toast.error(jobStatus.errorMessage || 'Retry failed.');
    }

    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['va-failed-calls'] }),
      queryClient.invalidateQueries({ queryKey: ['va-payments'] }),
    ]);
  }, [jobId, jobStatus, queryClient]);

  const isRetryActive =
    jobStatus?.jobStatus === 'pending' || jobStatus?.jobStatus === 'processing';

  const startRetry = async () => {
    try {
      const result = await retryFailedQueueClaim(claimId);
      if (!result?.jobId) {
        toast.info(result?.msg || 'No retry job was started.');
        return;
      }

      terminalHandledRef.current = '';
      setJobId(result.jobId);
      toast.info(
        result.isExisting
          ? 'A retry is already in progress for this claim.'
          : 'Retry queued for this claim.',
      );
    } catch (e) {
      toast.error(e?.response?.data?.msg || e.message || 'Failed to queue retry.');
    }
  };

  const buttonLabel =
    jobStatus?.jobStatus === 'pending'
      ? 'Queued...'
      : jobStatus?.jobStatus === 'processing'
        ? 'Retrying...'
        : 'Retry Call';

  return (
    <Button
      size="sm"
      variant="outline-secondary"
      disabled={disabled || isRetryActive}
      onClick={startRetry}
    >
      {buttonLabel}
    </Button>
  );
};

const BulkRetryStatusAlert = ({ jobId, jobStatus }) => {
  if (!jobId) return null;

  if (!jobStatus) {
    return (
      <Alert variant="info" className="mt-3 mb-0">
        Bulk retry queued. Waiting for job status...
      </Alert>
    );
  }

  const isActive =
    jobStatus.jobStatus === 'pending' || jobStatus.jobStatus === 'processing';
  const progressPercent = getJobProgressPercent(jobStatus);
  const result = jobStatus.result || {};

  if (isActive) {
    return (
      <Alert variant="info" className="mt-3 mb-0">
        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
          <div>
            <strong>Bulk Retry Running</strong>
            <span className="ms-2">
              {Number(jobStatus.processedItems || 0)} / {Number(jobStatus.totalItems || 0)} claims
            </span>
          </div>
          {jobStatus.jobStatus === 'pending' ? (
            <Badge bg="secondary">Queued</Badge>
          ) : (
            <Badge bg="primary">Processing</Badge>
          )}
        </div>
        {Number(jobStatus.totalItems || 0) > 0 ? (
          <ProgressBar
            animated={true}
            striped={true}
            now={progressPercent}
            label={`${progressPercent}%`}
            className="mt-2"
          />
        ) : null}
      </Alert>
    );
  }

  if (jobStatus.jobStatus === 'failed') {
    return (
      <Alert variant="danger" className="mt-3 mb-0">
        <strong>Bulk Retry Failed</strong>
        <span className="ms-2">{jobStatus.errorMessage || 'Unknown error'}</span>
      </Alert>
    );
  }

  return (
    <Alert variant="success" className="mt-3 mb-0">
      <strong>Bulk Retry Complete</strong>
      <span className="ms-2">
        {Number(result.recoveredClaims || 0)} recovered,
        {' '}
        {Number(result.remainingFailedClaims || 0)} still failed,
        {' '}
        {Number(result.skippedClaims || 0)} skipped,
        {' '}
        {Number(result.failedClaims || 0)} errored
      </span>
    </Alert>
  );
};

const VaFailedCallsQueue = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useUser();
  // Applying a rating mints an invoice, so the route needs billing:manage.
  // Hide the column for viewers who would only ever get a 403 from it.
  const canApplyBillingRating = hasPermission('billing:manage');
  const [filters, setFilters] = useState({
    page: 1,
    pageSize: 25,
    startDate: '',
    endDate: '',
    claimStatus: '',
    name: '',
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [nextRating, setNextRating] = useState('');
  const [isApplyingRating, setIsApplyingRating] = useState(false);
  const [activeBulkJobId, setActiveBulkJobId] = useState(null);
  const [isStartingBulkRetry, setIsStartingBulkRetry] = useState(false);
  const bulkTerminalHandledRef = useRef('');

  const { isLoading, data } = useFetchFailedVaCalls(
    filters.page,
    filters.pageSize,
    filters.startDate,
    filters.endDate,
    filters.claimStatus || null,
    filters.name || null,
  );
  const { data: bulkJobStatus } = useFetchJobStatus(activeBulkJobId);

  useEffect(() => {
    if (!activeBulkJobId || !bulkJobStatus) return;
    if (bulkJobStatus.jobStatus !== 'completed' && bulkJobStatus.jobStatus !== 'failed') return;

    const terminalKey = `${activeBulkJobId}:${bulkJobStatus.jobStatus}:${bulkJobStatus.completedAt || ''}`;
    if (bulkTerminalHandledRef.current === terminalKey) return;
    bulkTerminalHandledRef.current = terminalKey;

    if (bulkJobStatus.jobStatus === 'completed') {
      const result = bulkJobStatus.result || {};
      toast.success(
        `Bulk retry finished: ${Number(result.recoveredClaims || 0)} claims recovered.`,
      );
    } else {
      toast.error(bulkJobStatus.errorMessage || 'Bulk retry failed.');
    }

    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['va-failed-calls'] }),
      queryClient.invalidateQueries({ queryKey: ['va-payments'] }),
    ]);
  }, [activeBulkJobId, bulkJobStatus, queryClient]);

  const rows = data && data.rows ? data.rows : [];
  const totals = data && data.totals ? data.totals : {};
  const pagination = data && data.pagination ? data.pagination : { total: 0 };
  const isBulkRetryActive =
    bulkJobStatus?.jobStatus === 'pending' || bulkJobStatus?.jobStatus === 'processing';

  const openRatingModal = (row) => {
    const currentRating = Number(row.currentRating || 0);
    const defaultRating = RATING_OPTIONS.find((rating) => rating > currentRating) || '';
    setSelectedRow(row);
    setNextRating(defaultRating ? String(defaultRating) : '');
  };

  const closeRatingModal = (force = false) => {
    if (isApplyingRating && !force) return;
    setSelectedRow(null);
    setNextRating('');
  };

  const startBulkRetry = async () => {
    try {
      setIsStartingBulkRetry(true);
      const result = await retryAllFailedQueueClaims({
        startDate: filters.startDate,
        endDate: filters.endDate,
        claimStatus: filters.claimStatus || '',
        name: filters.name || '',
      });

      if (!result?.jobId) {
        toast.info(result?.msg || 'No failed queue claims match the current filters.');
        return;
      }

      bulkTerminalHandledRef.current = '';
      setActiveBulkJobId(result.jobId);
      toast.info(
        result.isExisting
          ? 'A bulk retry job is already running.'
          : `Queued ${Number(result.totalItems || 0)} claims for retry.`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.msg || e.message || 'Failed to queue bulk retry.');
    } finally {
      setIsStartingBulkRetry(false);
    }
  };

  const confirmApplyRating = async () => {
    if (!selectedRow) return;
    const parsedRating = Number(nextRating);
    const currentRating = Number(selectedRow.currentRating || 0);

    if (!parsedRating || parsedRating <= currentRating) {
      toast.error(`Choose a new rating above the current rating (${currentRating}%).`);
      return;
    }

    try {
      setIsApplyingRating(true);
      await applyFailedQueueRating(selectedRow.claimId, parsedRating);
      toast.success(`Applied ${parsedRating}% rating and triggered billing flow.`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['va-failed-calls'] }),
        queryClient.invalidateQueries({ queryKey: ['va-payments'] }),
      ]);
      closeRatingModal(true);
    } catch (e) {
      toast.error(e?.response?.data?.msg || e.message || 'Failed to apply rating.');
    } finally {
      setIsApplyingRating(false);
    }
  };

  return (
    <PrivateLayout>
      <Row>
        <Col>
          <Panel>
            <Row>
              <Col>
                <h2>Failed Calls Queue</h2>
                <div className="text-muted">
                  Claims with more than 3 consecutive failed VA call attempts. Automated calling is paused.
                </div>
                <div className="text-muted small">
                  Manual retries bypass schedule-day restrictions. A successful `ok` call resolves
                  the claim's rating reconciliation.
                </div>
              </Col>
              <Col xs={12} md={8} className="text-end">
                <div className="d-flex gap-2 justify-content-end align-items-center flex-wrap">
                  <Form.Control
                    type="search"
                    placeholder="Search by name or email"
                    value={filters.name}
                    onChange={(e) => setFilters({ ...filters, page: 1, name: e.target.value })}
                    style={{ maxWidth: 260 }}
                  />
                  <Form.Control
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters({ ...filters, page: 1, startDate: e.target.value })}
                    style={{ maxWidth: 160 }}
                  />
                  <Form.Control
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters({ ...filters, page: 1, endDate: e.target.value })}
                    style={{ maxWidth: 160 }}
                  />
                  <Form.Select
                    value={filters.claimStatus}
                    onChange={(e) => setFilters({ ...filters, page: 1, claimStatus: e.target.value })}
                    style={{ maxWidth: 180 }}
                  >
                    <option value="">All Statuses</option>
                    {COLUMNS.filter((c) => c.id !== 'leads').map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Button
                    variant="primary"
                    disabled={isStartingBulkRetry || isBulkRetryActive}
                    onClick={startBulkRetry}
                  >
                    {isBulkRetryActive ? 'Retry Running...' : 'Retry All Matching Filters'}
                  </Button>
                  <Button variant="outline-secondary" onClick={() => navigate('/admin/va_calls')}>
                    VA Call Attempts
                  </Button>
                  <Button variant="outline-secondary" onClick={() => navigate('/admin/va_payments')}>
                    VA Calls
                  </Button>
                </div>
              </Col>
            </Row>

            <BulkRetryStatusAlert jobId={activeBulkJobId} jobStatus={bulkJobStatus} />

            <Row className="mt-3">
              <Col>
                <div className="d-flex gap-4 flex-wrap">
                  <div>
                    <div className="text-muted">Claims in Queue</div>
                    <div className="fs-5 fw-bold">{Number(totals.totalClaims || 0)}</div>
                  </div>
                  <div>
                    <div className="text-muted">No Info</div>
                    <div className="fs-5 fw-bold">{Number(totals.noInfoCount || 0)}</div>
                  </div>
                  <div>
                    <div className="text-muted">System Unavailable</div>
                    <div className="fs-5 fw-bold">{Number(totals.sysUnavailCount || 0)}</div>
                  </div>
                  <div>
                    <div className="text-muted">Error</div>
                    <div className="fs-5 fw-bold">{Number(totals.errorCount || 0)}</div>
                  </div>
                </div>
              </Col>
            </Row>

            <br />

            <Table hover={true}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Claim Status</th>
                  <th>Current Rating</th>
                  <th>Failure Streak</th>
                  <th>Owner</th>
                  <th>Retry Call</th>
                  {canApplyBillingRating ? <th>Apply Rating</th> : null}
                </tr>
              </thead>
              <tbody>
                {!isLoading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={canApplyBillingRating ? 7 : 6}>
                      <p className="lead text-center mb-0">No Results Found</p>
                    </td>
                  </tr>
                ) : null}
                {rows.map((row) => {
                  const claimStatusLabel = COLUMNS.find((c) => c.id === row.claimStatus)?.label;
                  const currentRating = row.currentRating != null ? Number(row.currentRating) : null;
                  const canApplyRating = currentRating == null || currentRating < 100;
                  return (
                    <tr key={row.claimId}>
                      <td>
                        {row.claimId ? (
                          <a href={`/admin/profile/${row.claimId}`}>
                            {(row.userFirstName || '') + ' ' + (row.userLastName || '')}
                          </a>
                        ) : (
                          (row.userFirstName || '') + ' ' + (row.userLastName || '')
                        )}
                      </td>
                      <td>{claimStatusLabel || row.claimStatus || ''}</td>
                      <td>
                        {currentRating != null ? (
                          <Badge bg="dark">{currentRating}%</Badge>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <Badge bg="danger">{Number(row.consecutiveFailureCount || 0)}</Badge>
                      </td>
                      <td>
                        <div>{row.ownerName || 'Onboarding Admin'}</div>
                        <div className="text-muted small">
                          {ownerRoleLabel(row.ownerRole)}
                        </div>
                      </td>
                      <td>
                        <ClaimRetryButton
                          claimId={row.claimId}
                          disabled={isBulkRetryActive}
                        />
                      </td>
                      {canApplyBillingRating ? (
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            disabled={!canApplyRating || isBulkRetryActive}
                            onClick={() => openRatingModal(row)}
                          >
                            Set Rating
                          </Button>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            {pagination.total > 0 ? (
              <Pagination
                page={filters.page}
                totalRows={pagination.total}
                rowsPerPage={filters.pageSize}
                onBack={() => setFilters({ ...filters, page: filters.page - 1 })}
                onForward={() => setFilters({ ...filters, page: filters.page + 1 })}
              />
            ) : null}
          </Panel>
        </Col>
      </Row>

      <Modal show={!!selectedRow} onHide={closeRatingModal} centered={true}>
        <Modal.Header closeButton={!isApplyingRating}>
          <Modal.Title>Apply Manual Rating</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRow ? (
            <>
              <div className="mb-3">
                <strong>
                  {(selectedRow.userFirstName || '') + ' ' + (selectedRow.userLastName || '')}
                </strong>
              </div>
              <div className="mb-2">
                Current rating:{' '}
                {selectedRow.currentRating != null ? `${Number(selectedRow.currentRating)}%` : '-'}
              </div>
              <Form.Group className="mb-3">
                <Form.Label>New rating</Form.Label>
                <Form.Select
                  value={nextRating}
                  onChange={(e) => setNextRating(e.target.value)}
                  disabled={isApplyingRating}
                >
                  <option value="">Select a new rating</option>
                  {RATING_OPTIONS
                    .filter((rating) => selectedRow.currentRating == null || rating > Number(selectedRow.currentRating))
                    .map((rating) => (
                      <option key={rating} value={rating}>
                        {rating}%
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
              <div className="text-muted">
                This will create an admin-triggered VA payment record and run the same billing flow
                used for an automatic rating increase, including invoice/event side effects.
              </div>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="link" onClick={closeRatingModal} disabled={isApplyingRating}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmApplyRating} disabled={isApplyingRating || !nextRating}>
            {isApplyingRating ? 'Applying...' : 'Confirm and Apply'}
          </Button>
        </Modal.Footer>
      </Modal>
    </PrivateLayout>
  );
};

export default VaFailedCallsQueue;
